import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Button,
  ButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Divider,
  Chip,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, subDays } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { type AppDispatch, type RootState } from '../../../store';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import SpeedIcon from '@mui/icons-material/Speed';
import FavoriteIcon from '@mui/icons-material/Favorite';
import CalculateIcon from '@mui/icons-material/Calculate';
import EditIcon from '@mui/icons-material/Edit';
import SettingsIcon from '@mui/icons-material/Settings';
import SaveIcon from '@mui/icons-material/Save';

// RTK Query Hooks
import {
  useGetTrainingLoadQuery,
  useGetPerformanceMetricsQuery,
  useGetPowerCurvesQuery,
  useGetVO2EstimationQuery,
  useGetHeartRateZonesQuery,
  useGetPaceZonesQuery,
  useCalculateHeartRateZonesMutation,
  useCalculatePaceZonesMutation,
  useSetManualHeartRateZonesMutation,
  useSetManualPaceZonesMutation,
  useGetWorkoutSummaryQuery,
} from '../../../store/api/baseApi';
import KPICard from '../components/KPICard';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import type { 
  UserPaceZonesResponse, 
  UserHeartZonesResponse, 
  HRZoneCalculationRequest, 
  PaceZoneCalculationRequest,
  ManualHRZoneOverride,
  ManualPaceZoneOverride,
  HRZone,
  PaceZone
} from '../../../store/api/models/analyticsModels';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`analytics-tabpanel-${index}`}
      aria-labelledby={`analytics-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

// --- Sub-Component: HR Zone Calculation Dialog ---
interface HRZoneCalcDialogProps {
  open: boolean;
  onClose: () => void;
  onCalculate: (params: HRZoneCalculationRequest) => void;
  isLoading: boolean;
  userMaxHR?: number;
}

const HRZoneCalcDialog: React.FC<HRZoneCalcDialogProps> = ({ open, onClose, onCalculate, isLoading, userMaxHR }) => {
  const [method, setMethod] = useState<HRZoneCalculationRequest['method']>('max_hr');
  const [maxHr, setMaxHr] = useState(userMaxHR || 190);
  const [lthr, setLthr] = useState('');
  const [restHr, setRestHr] = useState('');

  useEffect(() => {
    if (open) {
      setMaxHr(userMaxHR || 190);
      setLthr('');
      setRestHr('');
    }
  }, [open, userMaxHR]);

  const handleConfirm = () => {
    const payload: HRZoneCalculationRequest = {
      method,
      params: {},
      activity_ids: [] 
    };

    if (method === 'max_hr') {
      payload.params = { max_hr: maxHr };
    } else if (method === 'lthr') {
      payload.params = { lthr: parseFloat(lthr) };
    } else if (method === 'hrr') {
      payload.params = { max_hr: maxHr, resting_hr: parseFloat(restHr) };
    }

    onCalculate(payload);
    onClose();
  };

  const isFormValid = () => {
    if (method === 'max_hr') return maxHr > 0;
    if (method === 'lthr') return lthr !== '';
    if (method === 'hrr') return maxHr > 0 && restHr !== '';
    return false;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Calculate Heart Rate Zones</DialogTitle>
      <DialogContent>
        <FormControl fullWidth margin="normal">
          <InputLabel>Calculation Method</InputLabel>
          <Select
            value={method}
            label="Calculation Method"
            onChange={(e) => setMethod(e.target.value as any)}
          >
            <MenuItem value="max_hr">Max Heart Rate (Percentage)</MenuItem>
            <MenuItem value="lthr">Lactate Threshold (Joe Friel)</MenuItem>
            <MenuItem value="hrr">Heart Rate Reserve (Karvonen)</MenuItem>
          </Select>
        </FormControl>

        <TextField
          margin="normal"
          label="Max Heart Rate (bpm)"
          type="number"
          fullWidth
          value={maxHr}
          onChange={(e) => setMaxHr(Number(e.target.value))}
          helperText="Required for all methods"
        />

        {method === 'hrr' && (
          <TextField
            margin="normal"
            label="Resting Heart Rate (bpm)"
            type="number"
            fullWidth
            value={restHr}
            onChange={(e) => setRestHr(e.target.value)}
          />
        )}

        {method === 'lthr' && (
          <TextField
            margin="normal"
            label="LTHR (bpm)"
            type="number"
            fullWidth
            value={lthr}
            onChange={(e) => setLthr(e.target.value)}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>Cancel</Button>
        <Button onClick={handleConfirm} variant="contained" disabled={!isFormValid() || isLoading}>
          {isLoading ? <CircularProgress size={20} /> : 'Calculate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// --- Sub-Component: Pace Zone Calculation Dialog ---
interface PaceZoneCalcDialogProps {
  open: boolean;
  onClose: () => void;
  onCalculate: (params: PaceZoneCalculationRequest) => void;
  isLoading: boolean;
}

const PaceZoneCalcDialog: React.FC<PaceZoneCalcDialogProps> = ({ open, onClose, onCalculate, isLoading }) => {
  const [referencePace, setReferencePace] = useState('5:00');
  const [units, setUnits] = useState('km');

  // Validate MM:SS format
  const isValidPaceFormat = (pace: string): boolean => {
    const mmssRegex = /^\d{1,2}:[0-5]\d$/;
    return mmssRegex.test(pace);
  };

  const handleConfirm = () => {
    if (!isValidPaceFormat(referencePace)) {
      alert('Please enter pace in MM:SS format (e.g., 5:30)');
      return;
    }

    const payload: PaceZoneCalculationRequest = {
      params: {
        reference_pace: referencePace,  // Send as MM:SS string
        units: units,
      },
      activity_ids: []
    };
    onCalculate(payload);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Calculate Pace Zones</DialogTitle>
      <DialogContent>
        <TextField
          margin="normal"
          label="Reference Pace (MM:SS)"
          type="text"
          fullWidth
          value={referencePace}
          onChange={(e) => setReferencePace(e.target.value)}
          helperText="Enter pace in MM:SS format (e.g., 5:30 for 5 minutes 30 seconds)"
          error={referencePace !== '' && !isValidPaceFormat(referencePace)}
        />
        <FormControl fullWidth margin="normal">
          <InputLabel>Units</InputLabel>
          <Select
            value={units}
            label="Units"
            onChange={(e) => setUnits(e.target.value)}
          >
            <MenuItem value="km">Per Kilometer</MenuItem>
            <MenuItem value="mile">Per Mile</MenuItem>
            <MenuItem value="100m">Per 100 Meters</MenuItem>
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>Cancel</Button>
        <Button onClick={handleConfirm} variant="contained" disabled={!referencePace || !isValidPaceFormat(referencePace) || isLoading}>
          {isLoading ? <CircularProgress size={20} /> : 'Calculate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// --- Sub-Component: Manual HR Zone Dialog ---
interface ManualHRDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: ManualHRZoneOverride) => void;
  isLoading: boolean;
  initialData?: UserHeartZonesResponse;
}

const ManualHRDialog: React.FC<ManualHRDialogProps> = ({ open, onClose, onSave, isLoading, initialData }) => {
  const [method, setMethod] = useState<'max_hr' | 'hrr' | 'lthr'>('max_hr');
  const [maxHr, setMaxHr] = useState('');
  const [restHr, setRestHr] = useState('');
  const [lthr, setLthr] = useState('');
  const [zones, setZones] = useState<HRZone[]>([]);

  useEffect(() => {
    if (open && initialData) {
      setMethod(initialData.method as any);
      setMaxHr(initialData.params?.max_hr?.toString() ?? '');
      setRestHr(initialData.params?.resting_hr?.toString() ?? '');
      setLthr(initialData.params?.lthr?.toString() ?? '');
      setZones([...initialData.zones]);
    } else if (open) {
      // Defaults if no data - Updated property names to match HRZone interface
      setZones([
        { zone: 1, min_hr: 0, max_hr: 142, name: 'Recovery' },
        { zone: 2, min_hr: 142, max_hr: 155, name: 'Endurance' },
        { zone: 3, min_hr: 155, max_hr: 162, name: 'Tempo' },
        { zone: 4, min_hr: 162, max_hr: 174, name: 'Threshold' },
        { zone: 5, min_hr: 174, max_hr: 220, name: 'VO2 Max' },
      ]);
    }
  }, [open, initialData]);

  const handleZoneChange = (index: number, field: 'min_hr' | 'max_hr', value: string) => {
    const numVal = parseInt(value, 10);
    if (!isNaN(numVal)) {
      const newZones = [...zones];
      newZones[index] = { ...newZones[index], [field]: numVal };
      setZones(newZones);
    }
  };

  const handleSave = () => {
    const params: Record<string, any> = {};
    if (method === 'max_hr') params.max_hr = parseInt(maxHr);
    if (method === 'hrr') { params.max_hr = parseInt(maxHr); params.resting_hr = parseInt(restHr); }
    if (method === 'lthr') params.lthr = parseInt(lthr);

    onSave({
      method,
      params,
      zones
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Manually Configure Heart Rate Zones</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={4}>
            <FormControl fullWidth>
              <InputLabel>Method</InputLabel>
              <Select value={method} label="Method" onChange={(e) => setMethod(e.target.value as any)}>
                <MenuItem value="max_hr">Max HR</MenuItem>
                <MenuItem value="hrr">HRR</MenuItem>
                <MenuItem value="lthr">LTHR</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {(method === 'max_hr' || method === 'hrr') && (
            <Grid item xs={4}>
              <TextField label="Max HR" type="number" fullWidth value={maxHr} onChange={e => setMaxHr(e.target.value)} />
            </Grid>
          )}
          {method === 'hrr' && (
            <Grid item xs={4}>
              <TextField label="Resting HR" type="number" fullWidth value={restHr} onChange={e => setRestHr(e.target.value)} />
            </Grid>
          )}
          {method === 'lthr' && (
             <Grid item xs={4}>
              <TextField label="LTHR" type="number" fullWidth value={lthr} onChange={e => setLthr(e.target.value)} />
            </Grid>
          )}
        </Grid>
        
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" gutterBottom>Zone Limits (BPM)</Typography>
        {zones.map((zone, idx) => (
          <Box key={zone.zone} sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 1 }}>
            <Typography sx={{ width: 120 }}>Zone {zone.zone}</Typography>
            <TextField label="Min" type="number" size="small" value={zone.min_hr} onChange={e => handleZoneChange(idx, 'min_hr', e.target.value)} sx={{ width: 100 }} />
            <TextField label="Max" type="number" size="small" value={zone.max_hr} onChange={e => handleZoneChange(idx, 'max_hr', e.target.value)} sx={{ width: 100 }} />
            </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={isLoading} startIcon={isLoading ? <CircularProgress size={20}/> : <SaveIcon />}>Save Changes</Button>
      </DialogActions>
    </Dialog>
  );
};

// --- Sub-Component: Manual Pace Zone Dialog ---
interface ManualPaceDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: ManualPaceZoneOverride) => void;
  isLoading: boolean;
  initialData?: UserPaceZonesResponse;
}

const ManualPaceDialog: React.FC<ManualPaceDialogProps> = ({ open, onClose, onSave, isLoading, initialData }) => {
  const [referencePace, setReferencePace] = useState('');
  const [units, setUnits] = useState('km');
  const [zones, setZones] = useState<PaceZone[]>([]);

  // Validate MM:SS format
  const isValidPaceFormat = (pace: string): boolean => {
    const mmssRegex = /^\d{1,2}:[0-5]\d$/;
    return mmssRegex.test(pace);
  };

  useEffect(() => {
    if (open && initialData) {
      setReferencePace(initialData.reference_pace);
      setUnits(initialData.units);
      setZones([...initialData.zones]);
    } else if (open) {
      // Defaults if no data - Using MM:SS format
      setReferencePace('5:00');
      setZones([
        { zone: 1, min_pace: '6:00', max_pace: '7:00', name: 'Easy' },
        { zone: 2, min_pace: '5:00', max_pace: '6:00', name: 'Moderate' },
        { zone: 3, min_pace: '4:30', max_pace: '5:00', name: 'Tempo' },
        { zone: 4, min_pace: '4:00', max_pace: '4:30', name: 'Threshold' },
        { zone: 5, min_pace: '3:00', max_pace: '4:00', name: 'VO2 Max' },
      ]);
    }
  }, [open, initialData]);

  const handleZoneChange = (index: number, field: 'min_pace' | 'max_pace', value: string) => {
    const newZones = [...zones];
    newZones[index] = { ...newZones[index], [field]: value };
    setZones(newZones);
  };

  const handleSave = () => {
    // Validate all pace values
    if (!isValidPaceFormat(referencePace)) {
      alert('Reference pace must be in MM:SS format');
      return;
    }

    for (const zone of zones) {
      if (!isValidPaceFormat(zone.min_pace) || !isValidPaceFormat(zone.max_pace)) {
        alert(`Zone ${zone.zone} has invalid pace format. Please use MM:SS format.`);
        return;
      }
    }

    onSave({
      reference_pace: referencePace,
      units: units,
      zones
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Manually Configure Pace Zones</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
           <Grid item xs={6}>
            <TextField
              label="Reference Pace (MM:SS)"
              type="text"
              fullWidth
              value={referencePace}
              onChange={e => setReferencePace(e.target.value)}
              helperText="Enter pace in MM:SS format (e.g., 5:30)"
              error={referencePace !== '' && !isValidPaceFormat(referencePace)}
            />
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel>Units</InputLabel>
              <Select value={units} label="Units" onChange={(e) => setUnits(e.target.value)}>
                <MenuItem value="km">Per Kilometer</MenuItem>
                <MenuItem value="mile">Per Mile</MenuItem>
                <MenuItem value="100m">Per 100 Meters</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" gutterBottom>Zone Limits (MM:SS Format)</Typography>
        {zones.map((zone, idx) => (
          <Box key={zone.zone} sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 1 }}>
            <Typography sx={{ width: 120 }}>Zone {zone.zone}</Typography>
            <TextField
              label="Min"
              type="text"
              size="small"
              value={zone.min_pace}
              onChange={e => handleZoneChange(idx, 'min_pace', e.target.value)}
              sx={{ width: 120 }}
              error={!isValidPaceFormat(zone.min_pace)}
            />
            <TextField
              label="Max"
              type="text"
              size="small"
              value={zone.max_pace}
              onChange={e => handleZoneChange(idx, 'max_pace', e.target.value)}
              sx={{ width: 120 }}
              error={!isValidPaceFormat(zone.max_pace)}
            />
            </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={isLoading} startIcon={isLoading ? <CircularProgress size={20}/> : <SaveIcon />}>Save Changes</Button>
      </DialogActions>
    </Dialog>
  );
};

// --- Main Page Component ---

const AnalyticsHubPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  
  const { user } = useSelector((state: RootState) => state.auth);

  const [startDate, setStartDate] = useState<Date | null>(subDays(new Date(), 90));
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [tabValue, setTabValue] = useState(0);

  // Dialog States
  const [hrCalcDialogOpen, setHrCalcDialogOpen] = useState(false);
  const [paceCalcDialogOpen, setPaceCalcDialogOpen] = useState(false);
  const [hrManualDialogOpen, setHrManualDialogOpen] = useState(false);
  const [paceManualDialogOpen, setPaceManualDialogOpen] = useState(false);

  // Local state for immediate UI updates after calculation
  const [localPaceZones, setLocalPaceZones] = useState<UserPaceZonesResponse | null>(null);

  const startStr = startDate ? startDate.toISOString() : '';
  const endStr = endDate ? endDate.toISOString() : '';

  // --- API Hooks ---
  const { data: trainingLoadData, isLoading: isLoadingTL, isError: isErrorTL } = useGetTrainingLoadQuery(
    { start_date: startStr, end_date: endStr },
    { skip: !startStr || !endStr }
  );

  const { data: perfMetrics, isLoading: isLoadingPerf } = useGetPerformanceMetricsQuery(
    { start_date: startStr, end_date: endStr },
    { skip: !startStr || !endStr }
  );

  const { data: powerCurve, isLoading: isLoadingPC, isError: isErrorPC, error: errorPC } = useGetPowerCurvesQuery(
    { sport_type: 'Ride', days_back: 365 },
    { skip: tabValue !== 2 }
  );

  const { data: vo2Max, isLoading: isLoadingVO2 } = useGetVO2EstimationQuery(
    { days_back: 180 },
    { skip: tabValue !== 2 }
  );

  const { data: hrZones, isLoading: isLoadingHR, refetch: refetchHRZones } = useGetHeartRateZonesQuery(undefined, { skip: tabValue !== 0 });

  const { data: apiPaceZones, isLoading: isLoadingPace, refetch: refetchPaceZones } = useGetPaceZonesQuery(undefined, { skip: tabValue !== 1 });

  const displayPaceZones = localPaceZones || apiPaceZones;

  const { data: workoutSummary, isLoading: isLoadingWorkoutSummary } = useGetWorkoutSummaryQuery({ days: 30 });

  const [calculateHR, { isLoading: isCalculatingHR }] = useCalculateHeartRateZonesMutation();
  const [calculatePace, { isLoading: isCalculatingPace }] = useCalculatePaceZonesMutation();
  const [setManualHR, { isLoading: isSavingHR }] = useSetManualHeartRateZonesMutation();
  const [setManualPace, { isLoading: isSavingPace }] = useSetManualPaceZonesMutation();

  const isLoading = isLoadingTL || isLoadingPerf || isLoadingPC || isLoadingVO2 || isLoadingHR || isLoadingPace;
  const isError = isErrorTL;

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleCalculateHRZones = (params: HRZoneCalculationRequest) => {
    calculateHR(params)
      .unwrap()
      .then(() => refetchHRZones())
      .catch((err) => console.error('Failed to calculate HR zones:', err));
  };

  const handleCalculatePaceZones = (params: PaceZoneCalculationRequest) => {
    calculatePace(params)
      .unwrap()
      .then((response) => {
        const newZones: UserPaceZonesResponse = {
          user_id: 0,
          reference_pace: response.params.reference_pace,  // Already in MM:SS format
          units: response.params.units,
          zones: response.zones,  // Zones already have MM:SS format
          source: response.source,
          confidence_score: response.confidence_score,
          updated_at: new Date().toISOString(),
        };
        setLocalPaceZones(newZones);
        refetchPaceZones();
      })
      .catch((err) => console.error('Failed to calculate Pace zones:', err));
  };

  const handleSaveManualHR = (data: ManualHRZoneOverride) => {
    setManualHR(data)
      .unwrap()
      .then(() => refetchHRZones())
      .catch((err) => console.error(err));
  };

  const handleSaveManualPace = (data: ManualPaceZoneOverride) => {
    setManualPace(data)
      .unwrap()
      .then((response) => {
          setLocalPaceZones(response);
          refetchPaceZones();
      })
      .catch((err) => console.error(err));
  };

  // No longer needed - pace values are already in MM:SS format from API

  const getTrainingLoadChartData = () => {
    if (!trainingLoadData) return null;
    const labels = trainingLoadData.daily_metrics.map((m) => format(new Date(m.date), 'MMM dd'));
    return {
      labels,
      datasets: [
        { label: 'CTL (Fitness)', data: trainingLoadData.daily_metrics.map((m) => m.ctl), borderColor: 'rgb(75, 192, 192)', backgroundColor: 'rgba(75, 192, 192, 0.1)', tension: 0.4, fill: true },
        { label: 'ATL (Fatigue)', data: trainingLoadData.daily_metrics.map((m) => m.atl), borderColor: 'rgb(255, 99, 132)', backgroundColor: 'rgba(255, 99, 132, 0.1)', tension: 0.4, fill: true },
        { label: 'TSB (Form)', data: trainingLoadData.daily_metrics.map((m) => m.tsb), borderColor: 'rgb(255, 206, 86)', backgroundColor: 'rgba(255, 206, 86, 0.1)', tension: 0.4, fill: true },
      ],
    };
  };

  const getPowerCurveChartData = () => {
    if (!powerCurve) return null;
    const labels = Object.keys(powerCurve.curve_data).map((sec) => {
      const s = parseInt(sec);
      if (s < 60) return `${s}s`;
      return `${Math.floor(s / 60)}m`;
    });
    return { labels, datasets: [{ label: 'Best Power (W)', data: Object.values(powerCurve.curve_data), borderColor: 'rgb(153, 102, 255)', backgroundColor: 'rgba(153, 102, 255, 0.5)', tension: 0.1 }] };
  };

  const getHRZonesChartData = () => {
    if (!hrZones) return null;
    return {
      labels: hrZones.zones.map((z) => z.name),
      datasets: [{
        label: 'Max Heart Rate (BPM)', data: hrZones.zones.map((z) => z.max_hr),
        backgroundColor: ['rgba(75, 192, 192, 0.6)', 'rgba(54, 162, 235, 0.6)', 'rgba(255, 206, 86, 0.6)', 'rgba(255, 159, 64, 0.6)', 'rgba(255, 99, 132, 0.6)'],
      }],
    };
  };

  const getPaceZonesChartData = () => {
    if (!displayPaceZones) return null;
    // Convert MM:SS to seconds for visualization
    const paceToSeconds = (mmss: string): number => {
      const [mins, secs] = mmss.split(':').map(Number);
      return mins * 60 + secs;
    };
    return {
      labels: displayPaceZones.zones.map((z) => z.name),
      datasets: [{
        label: 'Max Pace (seconds)',
        data: displayPaceZones.zones.map((z) => paceToSeconds(z.max_pace)),
        backgroundColor: ['rgba(75, 192, 192, 0.6)', 'rgba(54, 162, 235, 0.6)', 'rgba(255, 206, 86, 0.6)', 'rgba(255, 159, 64, 0.6)', 'rgba(255, 99, 132, 0.6)'],
      }],
    };
  };

  const trainingLoadOptions = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'top' as const }, title: { display: true, text: 'Training Load (CTL/ATL/TSB)' }, tooltip: { mode: 'index' as const, intersect: false } }, scales: { y: { beginAtZero: false, title: { display: true, text: 'Training Stress Score' } } } };
  const powerCurveOptions = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'top' as const }, title: { display: true, text: 'Power Curve' } }, scales: { y: { beginAtZero: true, title: { display: true, text: 'Power (W)' } }, x: { title: { display: true, text: 'Duration' } } } };
  const vo2MaxOptions = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'top' as const }, title: { display: true, text: 'VO2 Max Trend' } }, scales: { y: { title: { display: true, text: 'VO2 Max (ml/kg/min)' } } } };
  const hrZonesOptions = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, title: { display: true, text: 'Heart Rate Zones Distribution' } }, scales: { y: { beginAtZero: true, title: { display: true, text: 'Heart Rate (BPM)' } } } };
  const paceZonesOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: { display: true, text: 'Pace Zones Distribution' },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            const seconds = context.parsed.y;
            const mins = Math.floor(seconds / 60);
            const secs = Math.floor(seconds % 60);
            return `${mins}:${secs.toString().padStart(2, '0')} per ${displayPaceZones?.units || 'km'}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: false,
        title: { display: true, text: 'Pace (seconds)' },
        ticks: {
          callback: function(value: any) {
            const mins = Math.floor(value / 60);
            const secs = Math.floor(value % 60);
            return `${mins}:${secs.toString().padStart(2, '0')}`;
          }
        }
      }
    }
  };

  const renderPerformanceCard = (label: string, value: string | number, change?: number) => {
    return (
      <Card>
        <CardContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>{label}</Typography>
          <Typography variant="h5" component="div" fontWeight="bold">{value}</Typography>
          {change !== undefined && (
            <Box display="flex" alignItems="center" mt={1}>
              {change >= 0 ? <TrendingUpIcon color="success" fontSize="small" /> : <TrendingDownIcon color="error" fontSize="small" />}
              <Typography variant="body2" color={change >= 0 ? 'success.main' : 'error.main'} sx={{ ml: 0.5 }}>{change > 0 ? '+' : ''}{change}%</Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  const ZoneActionBar: React.FC<{ type: 'hr' | 'pace', hasData: boolean }> = ({ type, hasData }) => {
    return (
      <Box>
        <ButtonGroup variant={hasData ? "outlined" : "contained"} size="small">
          <Button startIcon={<CalculateIcon />} onClick={() => type === 'hr' ? setHrCalcDialogOpen(true) : setPaceCalcDialogOpen(true)} disabled={(type === 'hr' && isCalculatingHR) || (type === 'pace' && isCalculatingPace)}>
            {hasData ? 'Recalculate' : 'Calculate Zones'}
          </Button>
          <Button startIcon={<EditIcon />} onClick={() => type === 'hr' ? setHrManualDialogOpen(true) : setPaceManualDialogOpen(true)} color={hasData ? "primary" : "secondary"}>
            Set Manually
          </Button>
        </ButtonGroup>
      </Box>
    );
  };

  if (isLoading && !trainingLoadData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <HRZoneCalcDialog open={hrCalcDialogOpen} onClose={() => setHrCalcDialogOpen(false)} onCalculate={handleCalculateHRZones} isLoading={isCalculatingHR} userMaxHR={user?.max_hr ?? undefined} />
        <PaceZoneCalcDialog open={paceCalcDialogOpen} onClose={() => setPaceCalcDialogOpen(false)} onCalculate={handleCalculatePaceZones} isLoading={isCalculatingPace} />
        
        <ManualHRDialog 
          open={hrManualDialogOpen} 
          onClose={() => setHrManualDialogOpen(false)} 
          onSave={handleSaveManualHR} 
          isLoading={isSavingHR}
          initialData={hrZones ?? undefined}
        />
        <ManualPaceDialog 
          open={paceManualDialogOpen} 
          onClose={() => setPaceManualDialogOpen(false)} 
          onSave={handleSaveManualPace} 
          isLoading={isSavingPace}
          initialData={displayPaceZones ?? undefined}
        />

        {isError && <Alert severity="error" sx={{ mb: 3 }}>Error loading analytics data. Please check your date range or connection.</Alert>}

        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">Analytics Hub</Typography>
          <Typography variant="body1" color="text.secondary" gutterBottom>Comprehensive performance metrics and training analytics</Typography>
        </Box>

        <Paper sx={{ mb: 3, p: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <DatePicker label="Start Date" value={startDate} onChange={(newValue) => setStartDate(newValue)} slotProps={{ textField: { fullWidth: true, size: 'small' } }} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <DatePicker label="End Date" value={endDate} onChange={(newValue) => setEndDate(newValue)} slotProps={{ textField: { fullWidth: true, size: 'small' } }} />
            </Grid>
          </Grid>
        </Paper>
        
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4} lg={2}>{renderPerformanceCard('Current CTL', trainingLoadData?.current_ctl?.toFixed(1) ?? '0')}</Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>{renderPerformanceCard('Current ATL', trainingLoadData?.current_atl?.toFixed(1) ?? '0')}</Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>{renderPerformanceCard('Current TSB (Form)', trainingLoadData?.current_tsb?.toFixed(1) ?? '0')}</Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>{renderPerformanceCard('Avg Weekly TSS', trainingLoadData?.average_weekly_tss?.toFixed(0) ?? '0')}</Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>{renderPerformanceCard('Estimated VO2 Max', vo2Max?.estimated_vo2_max?.toFixed(1) ?? '0')}</Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>{renderPerformanceCard('Activities Analyzed', powerCurve?.activities_analyzed?.toString() ?? '0')}</Grid>
        </Grid>

        {getTrainingLoadChartData() && (
          <Paper sx={{ mb: 3 }}>
            <Box sx={{ height: 400, p: 2 }}>
              <Line data={getTrainingLoadChartData()!} options={trainingLoadOptions} />
            </Box>
          </Paper>
        )}

        {/* Workout Analytics Quick View */}
        <Paper elevation={2} sx={{ mb: 3, p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" fontWeight="bold">
              Workout Analytics
            </Typography>
            <Button
              variant="outlined"
              endIcon={<ArrowForwardIcon />}
              onClick={() => navigate('/athlete/analytics/workouts')}
            >
              View Detailed Analytics
            </Button>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <KPICard
                title="Completed Workouts"
                value={workoutSummary?.completed_workouts ?? 0}
                subtitle="Last 30 Days"
                loading={isLoadingWorkoutSummary}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KPICard
                title="Completion Rate"
                value={`${(workoutSummary?.completion_rate ?? 0).toFixed(1)}%`}
                loading={isLoadingWorkoutSummary}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KPICard
                title="Total Distance"
                value={`${((workoutSummary?.total_distance ?? 0) / 1000).toFixed(1)} km`}
                loading={isLoadingWorkoutSummary}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KPICard
                title="Avg TSS"
                value={(workoutSummary?.average_tss ?? 0).toFixed(0)}
                loading={isLoadingWorkoutSummary}
              />
            </Grid>
          </Grid>
        </Paper>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="analytics tabs">
            <Tab icon={<FavoriteIcon />} label="Heart Rate Zones" />
            <Tab icon={<SpeedIcon />} label="Pace Zones" />
            <Tab icon={<FitnessCenterIcon />} label="Power Analysis" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          {hrZones === null && !isLoadingHR ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 300, gap: 2 }}>
              <Typography variant="h6">No Heart Rate Zones Configured</Typography>
              <ZoneActionBar type="hr" hasData={false} />
            </Box>
          ) : (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, height: 400, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6">Zone Distribution</Typography>
                    <ZoneActionBar type="hr" hasData={true} />
                  </Box>
                  <Box sx={{ flex: 1, position: 'relative' }}>
                    {getHRZonesChartData() ? <Bar data={getHRZonesChartData()!} options={hrZonesOptions} /> : <Box display="flex" justifyContent="center" alignItems="center" height="100%"><CircularProgress /></Box>}
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                  <Table stickyHeader>
                    <TableHead><TableRow><TableCell><strong>Zone</strong></TableCell><TableCell><strong>Range (bpm)</strong></TableCell><TableCell><strong>Description</strong></TableCell></TableRow></TableHead>
                    <TableBody>{hrZones ? hrZones.zones.map((zone) => <TableRow key={zone.zone} hover><TableCell>{zone.name}</TableCell><TableCell>{zone.min_hr} - {zone.max_hr}</TableCell><TableCell><Chip label={hrZones.source} size="small" variant="outlined" /></TableCell></TableRow>) : <TableRow><TableCell colSpan={3} align="center">No zones configured</TableCell></TableRow>}</TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {displayPaceZones === null && !isLoadingPace ? (
             <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 300, gap: 2 }}>
              <Typography variant="h6">No Pace Zones Configured</Typography>
              <ZoneActionBar type="pace" hasData={false} />
            </Box>
          ) : (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, height: 400, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6">Zone Distribution</Typography>
                    <ZoneActionBar type="pace" hasData={true} />
                  </Box>
                  <Box sx={{ flex: 1, position: 'relative' }}>
                    {getPaceZonesChartData() ? <Bar data={getPaceZonesChartData()!} options={paceZonesOptions} /> : <Box display="flex" justifyContent="center" alignItems="center" height="100%"><CircularProgress /></Box>}
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                  <Table stickyHeader>
                    <TableHead><TableRow><TableCell><strong>Zone</strong></TableCell><TableCell><strong>Min Pace</strong></TableCell><TableCell><strong>Max Pace</strong></TableCell><TableCell><strong>Source</strong></TableCell></TableRow></TableHead>
                    <TableBody>{displayPaceZones ? displayPaceZones.zones.map((zone) => <TableRow key={zone.zone} hover><TableCell>{zone.name || `Zone ${zone.zone}`}</TableCell><TableCell>{zone.min_pace}</TableCell><TableCell>{zone.max_pace}</TableCell><TableCell><Chip label={displayPaceZones.source} size="small" variant="outlined" /></TableCell></TableRow>) : <TableRow><TableCell colSpan={4} align="center">No pace zones configured</TableCell></TableRow>}</TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={8}>
              <Paper sx={{ p: 2, height: 400 }}>
                {getPowerCurveChartData() ? <Line data={getPowerCurveChartData()!} options={powerCurveOptions} /> : <Box display="flex" justifyContent="center" alignItems="center" height="100%">{isLoadingPC ? <CircularProgress /> : <Typography>No data available</Typography>}</Box>}
              </Paper>
            </Grid>
            <Grid item xs={12} lg={4}>
              <Paper sx={{ p: 2, height: 400, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                {vo2Max ? (
                  <>
                    <Typography variant="h3" color="primary" fontWeight="bold">{vo2Max.estimated_vo2_max.toFixed(1)}</Typography>
                    <Typography variant="h6" color="text.secondary">ml/kg/min</Typography>
                    <Typography variant="caption" sx={{ mt: 2 }}>Sample Size: {vo2Max.sample_size} activities</Typography>
                    <Typography variant="caption">Method: {vo2Max.estimation_method}</Typography>
                  </>
                ) : <CircularProgress />}
              </Paper>
            </Grid>
            <Grid item xs={12}>
              {isErrorPC && 'status' in errorPC && errorPC.status === 422 ? <Alert severity="info">No activities with power-data are present!</Alert> : (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead><TableRow><TableCell><strong>Duration</strong></TableCell><TableCell><strong>Best Power</strong></TableCell><TableCell><strong>CP Est.</strong></TableCell></TableRow></TableHead>
                    <TableBody>{powerCurve && Object.entries(powerCurve.curve_data).map(([duration, watts], index) => <TableRow key={index} hover><TableCell>{duration}s</TableCell><TableCell>{watts.toFixed(0)} W</TableCell><TableCell>{(watts * 0.95).toFixed(0)} W</TableCell></TableRow>)}</TableBody>
                  </Table>
                </TableContainer>
              )}
            </Grid>
          </Grid>
        </TabPanel>

      </Container>
    </LocalizationProvider>
  );
};

export default AnalyticsHubPage;